import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpHandler, HttpHeaders, HttpResponse, HttpRequest } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import * as swal from 'sweetalert';
import * as _ from 'lodash';
import { catchError, tap } from 'rxjs/operators';
import { SessionStorageService } from 'ngx-store';

import { AbpHttpInterceptor, AbpHttpConfiguration, IErrorInfo } from '@abp/abpHttpInterceptor';
import { LogService } from '@abp/log/log.service';
import { Correlator } from './correlator';
import { MessageService } from '@app/shared/common/services/message.service';
import { AppAuthService } from '@app/shared/common/auth/app-auth.service';

@Injectable()
export class CatalystHttpConfiguration extends AbpHttpConfiguration {

    constructor(
        _messageService: MessageService,
        _logService: LogService,
        private _correlator: Correlator
    ) {
        super(_messageService, _logService);
    }

    showError(error: IErrorInfo): any {
        let title, message;

        if (error.details) {
            message = error.details;
            title = error.message || this.defaultError.message;
        } else {
            message = error.message;
            title = this.defaultError.message;
        }

        swal({
            title: title,
            text: `${message} <div style="color: lightgray;position: absolute;font-size: xx-small;margin-top: 75px;">${this._correlator.Id}</div>`,
            html: true,
            type: 'error'
        });
    }
}

@Injectable()
export class InterceptedHttp extends AbpHttpInterceptor {

    constructor(
        private authService: AppAuthService,
        configuration: CatalystHttpConfiguration,
        private logService: LogService,
        private _correlator: Correlator,
        private sessionStorageService: SessionStorageService
    ) {
        super(configuration);
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        var interceptObservable = new Subject<HttpEvent<any>>();
        var modifiedRequest = this.normalizeRequestHeaders(request);

        next.handle(modifiedRequest).pipe(
            tap((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse) {
                    this.handleResponse(event);
                }
            }),
            catchError((error: any, caught: Observable<any>) => {
                if (error.status === 401) {
                    
                    this.sessionStorageService.set('logoutWarning', true);
                    this.authService.logout();

                    return of({});
                }

                return this.handleErrorResponse(error, interceptObservable);
            })
        )
            .subscribe((event: HttpEvent<any>) => {
                this.handleSuccessResponse(event, interceptObservable);
            });

        return interceptObservable;
    }

    protected normalizeRequestHeaders(request: HttpRequest<any>): HttpRequest<any> {
        let modifiedHeaders = request.headers
            .set('Pragma', 'no-cache')
            .set('Cache-Control', 'no-cache')
            .set('Expires', 'Sat, 01 Jan 2000 00:00:00 GMT');

        modifiedHeaders = this.addXRequestedWithHeader(modifiedHeaders);

        if (!request.params.get('omitAuthorizationHeader')) {
            modifiedHeaders = this.addAuthorizationHeaders(modifiedHeaders);
        }

        modifiedHeaders = this.addAspNetCoreCultureHeader(modifiedHeaders);
        modifiedHeaders = this.addAcceptLanguageHeader(modifiedHeaders);
        modifiedHeaders = this.addTenantIdHeader(modifiedHeaders);
        modifiedHeaders = this.addCorrelationHeader(modifiedHeaders);

        return request.clone({
            headers: modifiedHeaders
        });
    }

    private addCorrelationHeader(headers: HttpHeaders): HttpHeaders {
        headers = headers.set(Correlator.CORRELATION_ID_HEADER, this._correlator.generateId);

        return headers;
    }

    private handleResponse(response: HttpResponse<any>) {
        if (!response || !response.headers) {
            return;
        }

        const correlationId = response.headers.get(Correlator.CORRELATION_ID_HEADER);
        if (correlationId) {
            this._correlator.Id = correlationId;
        }
    }
}
